import axios from 'axios';
import { API_BASE_URL } from '../../config/env';
import { clearCustomerAuthFlow } from '../storage/customerFlow';
import { attachRoleAuthInterceptors } from './authInterceptors';

/**
 * Card tokenisation.
 *
 * No card data ever touches this application. The backend mints a hosted
 * session, the customer enters their card on the provider's page, and the
 * provider redirects back with a token reference. That keeps the site out of
 * PCI scope entirely, and it is the same flow the app uses — the app needs a
 * deep link (`shiftwagon:///payments/tokenized`) where the web can use an
 * ordinary route.
 */

const api = axios.create({ baseURL: API_BASE_URL });

attachRoleAuthInterceptors(api, {
  role: 'customer',
  clearSession: clearCustomerAuthFlow,
});

export interface StoredPaymentMethod {
  publicId: string;
  brand?: string | null;
  last4?: string | null;
  isDefault?: boolean;
}

/**
 * Saved cards, read from the user record rather than fetched separately —
 * the app reads the same field, and a second source could disagree with it.
 */
export function readPaymentMethods(user: unknown): StoredPaymentMethod[] {
  const record = (user ?? {}) as Record<string, unknown>;
  const raw = (record.paymentMethods ?? record.paymentMethodResponses ?? []) as unknown;

  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => entry as Record<string, unknown>)
    .filter((entry) => typeof entry?.publicId === 'string' && entry.publicId.trim())
    .map((entry) => ({
      publicId: String(entry.publicId).trim(),
      brand: (entry.brand as string) ?? null,
      last4: (entry.last4 as string) ?? null,
      isDefault: Boolean(entry.isDefault),
    }));
}

/** The card a booking would charge: the default, else the first. */
export function selectDefaultPaymentMethod(
  methods: StoredPaymentMethod[],
): StoredPaymentMethod | null {
  return methods.find((method) => method.isDefault) ?? methods[0] ?? null;
}

/**
 * Starts a hosted card-capture session and returns the URL to send the customer
 * to. `returnUrl` must be an absolute URL on this site.
 */
export async function createHostedCardSession(returnUrl: string): Promise<string> {
  const response = await api.post('user-management/api/payment-methods/session-link', {
    returnUrl,
  });

  const hostedUrl = (response.data as { hostedUrl?: string; error?: string })?.hostedUrl;

  if (!hostedUrl) {
    throw new Error('Could not start a secure payment session. Please try again.');
  }

  return hostedUrl;
}

export interface TokenizedCard {
  provider: string;
  externalId: string;
  last4: string;
  brand: string;
}

/** Persists a card the provider has just tokenised. */
export async function savePaymentMethod(userId: string, card: TokenizedCard): Promise<unknown> {
  const response = await api.post(`user-management/api/users/${encodeURIComponent(userId)}/payment-methods`, {
    ...card,
    isDefault: true,
  });

  return response.data;
}

export default api;
